<template>
  <div
    v-if="!loading"
    class="archive-organization-flow"
  >
    <ArchiveConsequencesDialog
      v-if="currentDialog === 'consequences'"
      :organization-id="organizationId"
      :usage-data="usageData"
      :action="action"
      :source="source"
      @next="next"
      @close-dialog="closeDialog"
    />

    <ArchiveOfferDiscountDialog
      v-if="currentDialog === 'offer_discount'"
      :organization-id="organizationId"
      :current-plan="currentPlan"
      :current-plan-details="currentPlanDetails"
      :plans="plans"
      :coupon="usageData.offer_discount_coupon"
      :action="action"
      :source="source"
      @next="next"
      @close-dialog="closeDialog"
    />

    <EssentialsOfferDialog
      v-if="currentDialog === 'essentials_offer'"
      :organization-id="organizationId"
      :offer="usageData.essentials_offer"
      :essentials-plan-price="essentialsPlanPrice"
      :action="action"
      :source="source"
      @next="next"
      @close-dialog="closeDialog"
    />

    <ArchiveOfferDialog
      v-if="currentDialog === 'offer'"
      :current-plan="currentPlan"
      :organization-id="organizationId"
      :plans="plans"
      :action="action"
      :source="source"
      :offer-sgt-essentials-plan="offerSgtEssentialsPlan"
      :essentials-plan-price="essentialsPlanPrice"
      @next="next"
      @close-dialog="closeDialog"
    />

    <ArchiveFeedbackDialog
      v-if="currentDialog === 'feedback'"
      :organization-id="organizationId"
      :app-sumo="appSumo"
      :action="action"
      :remove-card-after-cancel="removeCardAfterCancel"
      :source="source"
      @close-dialog="closeDialog"
    />
  </div>
</template>

<script>
import ArchiveConsequencesDialog from './ArchiveConsequencesDialog.vue';
import ArchiveFeedbackDialog from './ArchiveFeedbackDialog.vue';
import ArchiveOfferDialog from './ArchiveOfferDialog.vue';
import ArchiveOfferDiscountDialog from './ArchiveOfferDiscountDialog.vue';
import EssentialsOfferDialog from './EssentialsOfferDialog.vue';
import { OrganizationsResource } from '~/resources';

const FLOWS = {
  with_offer: ['consequences', 'offer', 'feedback'],
  // AR0228: replaces the downgrade step ('offer') for targeted annual VSMBs.
  with_essentials_offer: ['consequences', 'essentials_offer', 'feedback'],
  with_discount_offer: ['consequences', 'offer_discount', 'feedback'],
  without_offer: ['consequences', 'feedback'],
};

export default {
  name: 'ArchiveOrganizationFlow',
  components: {
    ArchiveConsequencesDialog,
    ArchiveFeedbackDialog,
    ArchiveOfferDialog,
    ArchiveOfferDiscountDialog,
    EssentialsOfferDialog,
  },
  props: {
    action: {
      type: String,
      default: 'archive',
    },
    appSumo: {
      type: Boolean,
      required: true,
    },
    currentPlan: {
      type: String,
      default: null,
    },
    currentPlanDetails: {
      type: Object,
      default: null,
    },
    hubstaffSgt: {
      type: Boolean,
      required: true,
    },
    offerSgtEssentialsPlan: {
      type: Boolean,
      default: false,
    },
    essentialsPlanPrice: {
      type: [Number, null],
      default: null,
    },
    organizationId: {
      type: Number,
      required: true,
    },
    plans: {
      type: Array,
      required: true,
    },
    removeCardAfterCancel: {
      type: Boolean,
      default: false,
    },
    userId: {
      type: Number,
      required: true,
    },
    source: {
      type: String,
      default: null,
    },
  },
  data: () => ({
    loading: true,
    currentDialog: 'consequences',
    usageData: {},
  }),
  computed: {
    currentFlow() {
      // AR0228 runs before the free-tier guard on purpose: the spec includes Starter Annual.
      if (this.usageData.essentials_offer) {
        return FLOWS.with_essentials_offer;
      }

      if (this.usageData.discount_offer_eligible) {
        return FLOWS.with_discount_offer;
      }

      // Plans considered "free tier" — no downgrade offer is shown for these
      const freeTierPlans = ['Free', 'Starter', 'Essentials', null];

      if (!this.hubstaffSgt || freeTierPlans.includes(this.currentPlan)) {
        return FLOWS.without_offer;
      }

      return FLOWS.with_offer;
    },
  },
  async mounted() {
    await this.fetchUsageData();
  },
  methods: {
    next() {
      const nextIndex = this.currentFlow.indexOf(this.currentDialog) + 1;

      this.currentDialog = this.currentFlow[nextIndex] || null;
    },
    closeDialog() {
      this.currentDialog = null;
    },
    async fetchUsageData() {
      try {
        this.loading = true;

        const payload = {
          source: this.action === 'archive' ? 'archive_organization' : 'cancel_subscription',
          ...(this.source && { page_source: this.source }),
        };
        const params = { id: this.organizationId };
        const response = await OrganizationsResource.usageData(params, payload);

        if (response) {
          this.usageData = response;
          this.loading = false;
        } else {
          throw new Error('Unable to fetch data for Organization usage.');
        }
      } catch (_error) {
        this.$appMessage('Unable to fetch data for the Organization usage. Try again later.');
      }
    },
  },
};
</script>
